import axiosInstance from '../../lib/axios';
import { cancellableGet } from '../../lib/cancellableGet';

// Three envelopes, deliberately not normalised: lists { data, total_records, ... },
// resources are bare objects, audit is { results }.
const ROOT = '/admin/chat';

export const fetchInternalNotes = (conversationId, cursor) =>
  cancellableGet(`${ROOT}/conversations/${conversationId}/notes/`, {
    params: { limit: 50, ...(cursor ? { cursor } : {}) },
  });

export const saveInternalNote = async (conversationId, { body, clientNoteId }) => {
  const response = await axiosInstance.post(`${ROOT}/conversations/${conversationId}/notes/`, {
    body,
    client_note_id: clientNoteId,
  });
  return response.data;
};

// One page of a conversation list, and of a transcript backfill.
const DEFAULT_PAGE_SIZE = 50;

// Current caller only; no roster scan or pagination.
export const fetchMyAgentStatus = () => cancellableGet(`${ROOT}/agents/me/status/`);

// Bare object, no success wrapper.
export const setAgentStatus = async (status) => {
  const response = await axiosInstance.patch(`${ROOT}/agents/me/status/`, { status });
  return response.data;
};

// Agents serve every queue, so `role` and `status` are the only filters.
export const fetchAgents = ({ role, status, page = 1, limit = DEFAULT_PAGE_SIZE } = {}) => {
  const params = { page, limit };
  if (role) params.role = role;
  if (status) params.status = status;
  return cancellableGet(`${ROOT}/agents/`, { params });
};

// The caller's own threads. `status` is a tab (all | open | waiting | closed), not a raw
// status; rows carry `status_group`. Support agent or admin only.
export const fetchConversations = ({ status, queue, page = 1, limit = DEFAULT_PAGE_SIZE } = {}) => {
  const params = { page, limit };
  // Omitting the tab means "all", so don't send the word.
  if (status && status !== 'all') params.status = status;
  if (queue) params.queue = queue;
  return cancellableGet(`${ROOT}/conversations/`, { params });
};

// Unassigned, claimable threads in one support queue, oldest wait first. Support agent or admin.
export const fetchPool = ({ queue, page = 1, limit = DEFAULT_PAGE_SIZE } = {}) =>
  cancellableGet(`${ROOT}/conversations/pool/`, { params: { queue, page, limit } });

// Coach tabs: all | unread | waiting. Rows carry the support `status_group`, so render the
// tab from the filter sent, not the row.
export const fetchCoachConversations = ({ status, page = 1, limit = DEFAULT_PAGE_SIZE } = {}) => {
  const params = { page, limit };
  if (status && status !== 'all') params.status = status;
  return cancellableGet(`${ROOT}/conversations/coach/`, { params });
};

// Closed threads of this conversation's customer; `queue` is required and excludes coaching.
export const fetchSupportHistory = (
  customerId,
  { queue = 'general', page = 1, limit = DEFAULT_PAGE_SIZE } = {}
) =>
  cancellableGet(`${ROOT}/customers/${customerId}/support-history/`, {
    params: { queue, page, limit },
  });

// Bare object. Carries the customer detail the list rows omit.
export const fetchConversation = (conversationId) =>
  cancellableGet(`${ROOT}/conversations/${conversationId}/`);

// Every score on a thread, newest first, names already filled in. `/api/schema/` declares
// a bare array here while the contract note shows the paginated envelope, so take either.
export const fetchConversationRatings = async (conversationId, { signal, limit = 50 } = {}) => {
  const response = await axiosInstance.get(`${ROOT}/conversations/${conversationId}/ratings/`, {
    params: { limit },
    signal,
  });
  const body = response.data;
  const rows = Array.isArray(body) ? body : body?.data || [];
  return { rows, total: Number.isFinite(body?.total_records) ? body.total_records : rows.length };
};

// Bare object. Empty body = self-assign; 409 CONFLICT_ERROR means another agent won the race.
export const assignConversation = async (conversationId, { agentId } = {}) => {
  const response = await axiosInstance.post(
    `${ROOT}/conversations/${conversationId}/assign/`,
    agentId ? { agent_id: agentId } : {}
  );
  return response.data;
};

// Paginated envelope, newest-first. Every call writes a transcript_viewed audit row -
// fetch on open and on reconnect only, never on a timer.
export const fetchMessages = (conversationId, { page = 1, limit = DEFAULT_PAGE_SIZE } = {}) =>
  cancellableGet(`${ROOT}/conversations/${conversationId}/messages/`, {
    params: { page, limit },
  });

// Bare object. Reuse client_msg_id on retry: the server dedupes and returns the original.
export const sendMessage = async (conversationId, payload) => {
  const response = await axiosInstance.post(
    `${ROOT}/conversations/${conversationId}/messages/`,
    payload
  );
  return response.data;
};

// Bare object. The wire field is `status`, not `outcome` - sending the wrong name
// silently falls back to the 'resolved' default.
export const resolveConversation = async (conversationId, { reason, status }) => {
  const body = { status };
  if (reason) body.reason = reason;
  const response = await axiosInstance.post(
    `${ROOT}/conversations/${conversationId}/resolve/`,
    body
  );
  return response.data;
};

// Bare object.
export const markConversationRead = async (conversationId) => {
  const response = await axiosInstance.post(`${ROOT}/conversations/${conversationId}/read/`, {});
  return response.data;
};

// Bare object.
export const transferConversation = async (conversationId, { agentId, reason }) => {
  const body = { agent_id: agentId };
  if (reason) body.reason = reason;
  const response = await axiosInstance.post(
    `${ROOT}/conversations/${conversationId}/transfer/`,
    body
  );
  return response.data;
};

// Bare object. Staff have no reopen; requeue is how a thread goes back to the pool.
// An omitted queue leaves the thread where it is.
export const requeueConversation = async (conversationId, { queue, reason } = {}) => {
  const body = {};
  if (queue) body.queue = queue;
  if (reason) body.reason = reason;
  const response = await axiosInstance.post(
    `${ROOT}/conversations/${conversationId}/requeue/`,
    body
  );
  return response.data;
};

// { results: [] }.
export const fetchConversationAudit = (conversationId) =>
  cancellableGet(`${ROOT}/conversations/${conversationId}/audit/`);
